import { Editor, Node, Operation, Text, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { Descendant, NodeEntry } from 'slate/dist/interfaces/node';

import { someNode } from '../../../../slatePlugins/common/queries/someNode';
import { insertBlankText } from '../../helpers/node';
import { isElementWithType } from '../../helpers/node-filter';
import { getAtEndEdgeOfBlock, isAtEndOf } from '../../helpers/selection';
import { ELEMENT_ROUNDED } from './defaults';
import {
  isFirstDeleteBackward,
  removeFirstDelete,
  setFirstDeleteBackward,
} from './transform/setFirstDelete';

const getFirstAndLastChild = (childEntries: Generator<NodeEntry<Descendant>, void, undefined>) => {
  let childIterator = childEntries.next();

  let first;
  if (childIterator.value) {
    first = childIterator.value;
    const [node] = first;
    if (Text.isText(node) && node.text.startsWith(' ')) {
      first = null;
    }
  }

  let last;
  do {
    last = childIterator.value;
    childIterator = childEntries.next();
  } while (!childIterator.done);

  if (last) {
    const [node] = last;
    if (Text.isText(node) && node.text.endsWith(' ')) {
      last = null;
    }
  }
  return { first, last, isEmpty: !first && !last };
};

export function isInsideOfElement<T extends Editor>(editor: T, type) {
  return someNode(editor, { match: { type } as any, block: true });
}

export function removeCarriageReturn(text: string) {
  return text.replace(/(\r\n|\n|\r)/gm, '');
}

const TEXT_CONTENT = 'text/plain';

export const withRoundedPlugin =
  (options?) =>
  <T extends ReactEditor>(editor: T) => {
    const { normalizeNode, insertBreak, deleteBackward, apply, insertData } = editor;

    editor.insertData = (data) => {
      if (isInsideOfElement(editor, ELEMENT_ROUNDED)) {
        const text = data.getData(TEXT_CONTENT);
        Transforms.insertText(editor, removeCarriageReturn(text));
      } else {
        insertData(data);
      }
    };

    editor.insertBreak = () => {
      if (isInsideOfElement(editor, ELEMENT_ROUNDED)) {
        return;
      }
      insertBreak();
    };

    editor.deleteBackward = (unit: 'character' | 'word' | 'line' | 'block') => {
      try {
        if (isAtEndOf(editor, ELEMENT_ROUNDED)) {
          const edgeInfo = getAtEndEdgeOfBlock(editor, { type: ELEMENT_ROUNDED }, 1);
          if (!edgeInfo || !edgeInfo.element) {
            return deleteBackward(unit);
          }

          const { element } = edgeInfo;
          if (isFirstDeleteBackward(element)) {
            Transforms.unwrapNodes(editor, { at: element[1] });
          } else {
            setFirstDeleteBackward(editor, { at: element[1] });
          }
          return;
        }
      } catch (error) {
        console.error('Error in deleteBackward:', error);
      }
      deleteBackward(unit);
    };

    editor.apply = (operation: Operation) => {
      // Apply the operation first to ensure editor state is updated
      apply(operation);

      // Then handle cleanup after the operation is applied
      try {
        if (operation.type === 'set_selection' && operation.properties && editor.selection) {
          // Only remove first delete markers if we're not currently within a rounded element
          // This prevents crashes when selecting within rounded elements
          const isInRounded = isInsideOfElement(editor, ELEMENT_ROUNDED);
          if (!isInRounded) {
            // Find all nodes that might have first delete markers
            const nodeEntries = Editor.nodes(editor, {
              match: (n) => isElementWithType(n, ELEMENT_ROUNDED),
            });

            // Process each found node
            for (const [, path] of nodeEntries) {
              removeFirstDelete(editor, { at: path });
            }
          }
        }
      } catch (error) {
        console.error('Error handling post-apply operations:', error);
      }
    };

    editor.normalizeNode = (entry) => {
      const [node, path] = entry;

      if (isElementWithType(node, ELEMENT_ROUNDED)) {
        try {
          const childEntries = Node.children(editor, path);
          const { first, last } = getFirstAndLastChild(childEntries);

          if (last) {
            const [, lastPath] = last;
            const newPath = [...lastPath];
            newPath[newPath.length - 1] = newPath[newPath.length - 1] + 1;
            insertBlankText(last, editor, { at: newPath });
          }

          if (first) {
            insertBlankText(first, editor);
          }

          // Add space at end of rounded if needed
          const nextNode = Editor.next(editor, { at: path });
          if (nextNode?.[0]?.text?.length === 0) {
            Transforms.insertText(editor, ' ', { at: nextNode[1] });
          }

          return;
        } catch (error) {
          console.error('Error normalizing rounded node:', error);
        }
      }

      normalizeNode(entry);
    };

    return editor;
  };
